// src/components/ClientPortalInvoiceDashboard.jsx

import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Check,
  AlertTriangle,
  Clock,
  Eye,
  FileDownload,
  ExternalLink,
  FileText,
} from "lucide-react"; // Added FileDownload and ExternalLink icons
import { useClientInvoices } from "../hooks/useInvoices"; // Or wherever your invoices hook is
import { useAuth } from "../hooks/useAuth";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useHeader } from "../context/HeaderContext";
import PaginatedInvoiceTable from "./PaginatedInvoiceTable";
import PaymentModal from "./PaymentModal";
import { useClientDocs } from "../hooks/useClientDocs";
import { authStorage } from "../functions/auth";
import { Card, StatCard, PageHeader, Spinner } from "./portal/PortalUI";

/**
 * This dashboard displays your client's Invoices in a style similar to your existing
 * ClientPortalDashboard component. The user can filter by status, search, and see
 * invoice details such as creation date, due date, status, etc.
 */
const ClientPortalInvoiceDashboard = () => {
  // Tab state: 'all', 'pending', 'paid', or 'overdue'
  const [activeTab, setActiveTab] = useState("all");

  // Search term for invoice_number or notes
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state for client portal
  const [page, setPage] = useState(0);
  const limit = 20; // Invoices per page

  // Payment modal state
  const [paymentInvoice, setPaymentInvoice] = useState(null);

  // Router & Auth
  const navigate = useNavigate();

  // Auth hook and user data (if needed)
  const { logout } = useAuth();
  const user = authStorage.getUserData() || {};

  // Header context (for putting a search bar in the header)
  const { setExtraHeaderContent } = useHeader();

  // 1) Pull client portal invoices from the updated useClientInvoices hook
  // Mapping the returned values to match existing variable names
  const {
    clientInvoices: clientPortalInvoices,
    pagination: clientPortalPagination,
    isLoading: isLoadingClientPortal,
    isError: isErrorClientPortal,
    error: errorClientPortal,
    refetch,
    createPaymentIntent,
    isCreatingPaymentIntent,
    invalidateInvoices,
  } = useClientInvoices({ page, limit });

  // 2) Use the useClientDocs hook to get the downloadDocument and getShareableLink mutations
  const {
    downloadDocument,
    isDownloading,
    downloadError,
    getShareableLink,
    isFetchingShareableLink,
    fetchShareableLinkError,
  } = useClientDocs();

  // 2) Insert a search bar into the header
  useEffect(() => {
    const searchBar = (
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-grayteck-400" />
        </div>
        <input
          type="text"
          placeholder="Search invoices"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9 pr-4 py-2 rounded-lg text-sm border border-grayteck-300 dark:border-darkbg-500
                     bg-white dark:bg-darkbg-700 text-grayteck-900 dark:text-grayteck-100
                     placeholder:text-grayteck-400 dark:placeholder:text-grayteck-500
                     focus:outline-none focus:ring-2 focus:ring-grayteck-600 dark:focus:ring-gold-400
                     focus:border-transparent transition-all duration-200 w-full sm:w-64"
        />
      </div>
    );
    setExtraHeaderContent(searchBar);
    return () => {
      setExtraHeaderContent(null);
    };
  }, [searchTerm, setExtraHeaderContent]);

  // 3) Helpers for invoice status
  const getInvoiceStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return "Draft";
      case "pending":
      case "opened": // Treat 'opened' as 'pending'
        return "Pending";
      case "paid":
        return "Paid";
      case "overdue":
        return "Overdue";
      case "canceled":
        return "Canceled";
      case "refunded":
        return "Refunded";
      default:
        return "Unknown";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "opened":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "overdue":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      case "canceled":
        return "bg-grayteck-100 text-grayteck-800 dark:bg-grayteck-900/30 dark:text-grayteck-300";
      case "refunded":
        return "bg-grayteck-100 text-grayteck-800 dark:bg-grayteck-900/30 dark:text-grayteck-300";
      case "draft":
      default:
        return "bg-grayteck-100 text-grayteck-800 dark:bg-grayteck-900/30 dark:text-grayteck-300";
    }
  };

  // 4) Stats: count 'all', 'pending', 'paid', 'overdue'
  const stats = useMemo(() => {
    if (!clientPortalInvoices) {
      return { all: 0, pending: 0, paid: 0, overdue: 0 };
    }
    return clientPortalInvoices.reduce(
      (acc, inv) => {
        acc.all++;
        if (inv.status === "pending") acc.pending++;
        if (inv.status === "opened") acc.pending++;
        if (inv.status === "paid") acc.paid++;
        if (inv.status === "overdue") acc.overdue++;
        return acc;
      },
      { all: 0, pending: 0, paid: 0, overdue: 0 }
    );
  }, [clientPortalInvoices]);

  // 5) Filter the invoices by activeTab and searchTerm
  const filteredInvoices = useMemo(() => {
    if (!clientPortalInvoices) return [];

    return clientPortalInvoices.filter((invoice) => {
      // Filter by active tab
      if (
        activeTab === "pending" &&
        invoice.status !== "pending" &&
        invoice.status !== "opened"
      )
        return false;
      if (activeTab === "paid" && invoice.status !== "paid") return false;
      if (activeTab === "overdue" && invoice.status !== "overdue") return false;
      // 'all' => no filter by status

      // Filter by search term (invoice_number or notes)
      if (searchTerm) {
        const haystack = (
          (invoice.invoice_number || "") +
          " " +
          (invoice.notes || "")
        ).toLowerCase();
        if (!haystack.includes(searchTerm.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }, [clientPortalInvoices, activeTab, searchTerm]);

  // 6) Loading / Error states for client portal
  if (isLoadingClientPortal) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner label="Loading your invoices…" />
      </div>
    );
  }

  if (isErrorClientPortal) {
    return (
      <div className="text-center mt-10 text-red-500">
        Error loading your invoices: {errorClientPortal?.message}
      </div>
    );
  }

  // 8) Handler for Open External Link
  const handleOpenExternalLink = async (documentId) => {
    // Open the tab synchronously, inside the click's gesture chain, so iOS
    // Safari and other mobile popup blockers don't kill it.
    const viewer = window.open("", "_blank");
    try {
      const data = await getShareableLink(documentId);
      if (data && data.share_url) {
        if (viewer) viewer.location = data.share_url;
      } else {
        toast.error("Shareable link not available");
        if (viewer) viewer.close();
      }
    } catch (error) {
      console.error("Error fetching shareable link:", error);
      // Error toast is already handled in the mutation
      if (viewer) viewer.close();
    }
  };

  // 9) Handler for Download Invoice
  const handleDownloadInvoice = async (invoiceDocId) => {
    try {
      await downloadDocument(invoiceDocId);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  // 10) Render Stats + Paginated Table
  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Invoices"
        subtitle="Track, review and pay invoices for your engagements"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5 mb-8">
        {[
          { id: "all", title: "All Invoices", count: stats.all, icon: FileText },
          { id: "pending", title: "Pending", count: stats.pending, icon: Clock },
          { id: "paid", title: "Paid", count: stats.paid, icon: Check },
          { id: "overdue", title: "Overdue", count: stats.overdue, icon: AlertTriangle },
        ].map((stat, i) => (
          <StatCard
            key={stat.id}
            title={stat.title}
            count={stat.count}
            icon={stat.icon}
            active={activeTab === stat.id}
            onClick={() => setActiveTab(stat.id)}
            delay={i * 0.05}
          />
        ))}
      </div>

      {/* Paginated Invoice Table */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative"
      >
        <PaginatedInvoiceTable
          invoices={filteredInvoices}
          isLoading={
            isLoadingClientPortal || isDownloading || isFetchingShareableLink
          }
          pagination={clientPortalPagination}
          onPageChange={(newPage) => setPage(newPage)}
          getInvoiceStatusColor={getStatusColor}
          onViewDetails={(invoice) => {
            // Example: navigate to a details page or open a modal
            navigate(`/client-portal/invoices/${invoice._id}`);
          }}
          onUnpublish={(invoice) => {
            // If you have an unpublish mutation, call it here
            toast.info(`Unpublish Invoice #${invoice.invoice_number}`);
          }}
          onSendInvoice={(invoice) => {
            // If you have a send mutation, call it here
            toast.info(`Send Invoice #${invoice.invoice_number}`);
          }}
          onOpenExternalLink={handleOpenExternalLink}
          onDownloadInvoice={handleDownloadInvoice}
          onPayInvoice={(inv) => setPaymentInvoice(inv)}
          context="client"
        />
      </motion.div>

      {/* Handle Download and Shareable Link Errors */}
      {(downloadError || fetchShareableLinkError) && (
        <div className="mt-4 text-center text-red-500">
          {downloadError &&
            `Error downloading invoice: ${downloadError.message}`}
          {fetchShareableLinkError &&
            `Error fetching shareable link: ${fetchShareableLinkError.message}`}
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={!!paymentInvoice}
        onClose={() => setPaymentInvoice(null)}
        invoice={paymentInvoice}
        createPaymentIntent={createPaymentIntent}
        isCreatingPaymentIntent={isCreatingPaymentIntent}
        onPaymentSuccess={() => {
          invalidateInvoices();
          toast.success("Payment successful!");
          setPaymentInvoice(null);
        }}
      />
    </div>
  );
};

export default ClientPortalInvoiceDashboard;
